import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import { useAuth } from "@context/AuthContext";
import { useTheme } from "@context/ThemeContext";

const PAGE_META = {
  "/dashboard":              { title: "Dashboard",       icon: "fa-table-cells-large" },
  "/account-settings":       { title: "Settings",        icon: "fa-gear"              },
  "/search":                 { title: "Search",          icon: "fa-magnifying-glass"  },
  "/search-v2":              { title: "Search",          icon: "fa-magnifying-glass"  },
  "/connectiq":              { title: "Connect IQ",      icon: "fa-robot"             },
  "/resources":              { title: "My Team",         icon: "fa-users"             },
  "/my-team":                { title: "My Team",         icon: "fa-users"             },
  "/edit-profile":           { title: "Edit Profile",    icon: "fa-pen-to-square"     },
  "/jobs":                   { title: "Jobs",            icon: "fa-briefcase"         },
  "/job/latest-jobs":        { title: "Jobs",            icon: "fa-briefcase"         },
  "/jobs/my-posted-jobs":    { title: "My Posted Jobs",  icon: "fa-pen-to-square"     },
  "/job/my-jobs":            { title: "My Posted Jobs",  icon: "fa-pen-to-square"     },
  "/create-job":             { title: "Post a Job",      icon: "fa-briefcase"         },
  "/create-job-manually":    { title: "Post a Job",      icon: "fa-briefcase"         },
  "/access-control":         { title: "Access Control",  icon: "fa-shield-halved"     },
  "/resource-managers":      { title: "Resource Managers", icon: "fa-user-tie"        },
  "/add-resource-manager":   { title: "Add Resource Manager", icon: "fa-user-plus"    },
  "/edit-firm":              { title: "Edit Firm",       icon: "fa-building"          },
  "/edit-firm-owner-profile":{ title: "Edit Profile",    icon: "fa-pen-to-square"     },
};

const W = { expanded: 260, collapsed: 64 };
const TRANSITION = "margin-left 0.22s cubic-bezier(0.4,0,0.2,1)";
const BTN_TRANSITION = "left 0.22s cubic-bezier(0.4,0,0.2,1)";

export default function DashboardLayout({ children }) {
  const [collapsed,   setCollapsed]   = useState(false);
  const [mobileOpen,  setMobileOpen]  = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  const location = useLocation();
  const navigate  = useNavigate();
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();

  const initials = user?.name ? user.name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase() : "U";

  const page = PAGE_META[location.pathname]
    || (location.pathname.startsWith("/profile/edit")   && { title: "Edit Profile", icon: "fa-pen-to-square" })
    || (location.pathname.startsWith("/profile/")       && { title: "Profile",      icon: "fa-circle-user"  })
    || (location.pathname.startsWith("/job/edit/")       && { title: "Edit Job",     icon: "fa-pen-to-square" })
    || (location.pathname.startsWith("/jobs/")           && { title: "Job Details",  icon: "fa-briefcase"    })
    || (location.pathname.startsWith("/job/details/")    && { title: "Job Details",  icon: "fa-briefcase"    })
    || (location.pathname.startsWith("/resources/edit-resource/") && { title: "Edit Profile", icon: "fa-pen-to-square" })
    || (location.pathname.startsWith("/my-team/edit-resource/")   && { title: "Edit Profile", icon: "fa-pen-to-square" })
    || (location.pathname.startsWith("/resources/")      && { title: "Candidate",    icon: "fa-user"         })
    || (location.pathname.startsWith("/my-team/")         && { title: "Candidate",    icon: "fa-user"         })
    || { title: "FirmConnector", icon: "fa-house" };

  const sidebarW = collapsed ? W.collapsed : W.expanded;

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "var(--fc-bg-page)" }}>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="d-lg-none" onClick={() => setMobileOpen(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 99, backdropFilter: "blur(2px)" }} />
      )}

      <Sidebar
        collapsed={collapsed}
        mobileOpen={mobileOpen}
        onClose={() => setMobileOpen(false)}
      />

      {/* ── Floating toggle circle — on sidebar edge, desktop only ── */}
      <button
        onClick={() => setCollapsed(c => !c)}
        aria-label="Toggle sidebar"
        className="d-none d-lg-flex align-items-center justify-content-center"
        style={{
          position: "fixed",
          top: 22,
          left: sidebarW - 12,
          width: 24,
          height: 24,
          borderRadius: "50%",
          background: "#1F2937",
          border: "2px solid #374151",
          cursor: "pointer",
          color: "#9CA3AF",
          fontSize: 8,
          zIndex: 102,
          boxShadow: "0 2px 8px rgba(0,0,0,0.4)",
          transition: `${BTN_TRANSITION}, background 0.15s, color 0.15s, border-color 0.15s`,
          flexShrink: 0,
        }}
        onMouseEnter={e => { e.currentTarget.style.background = "#2563EB"; e.currentTarget.style.color = "#fff"; e.currentTarget.style.borderColor = "#2563EB"; }}
        onMouseLeave={e => { e.currentTarget.style.background = "#1F2937"; e.currentTarget.style.color = "#9CA3AF"; e.currentTarget.style.borderColor = "#374151"; }}
      >
        <i className={`fa-solid fa-chevron-${collapsed ? "right" : "left"}`} />
      </button>

      {/* Main content */}
      <div className="fc-main" style={{ marginLeft: sidebarW, transition: TRANSITION }}>

        {/* Topbar */}
        <header className="fc-topbar">
          <div className="d-flex align-items-center gap-2 overflow-hidden">
            <button className="d-lg-none fc-icon-btn border-0 bg-transparent me-1 flex-shrink-0"
              onClick={() => setMobileOpen(true)} style={{ width: 40 }}>
              <i className="fa-solid fa-bars" style={{ fontSize: 16 }} />
            </button>
            <div className="d-flex align-items-center gap-2 overflow-hidden">
              <div className="fc-topbar-icon flex-shrink-0">
                <i className={`fa-solid ${page.icon}`} />
              </div>
              <span className="fc-topbar-title text-truncate">{page.title}</span>
            </div>
          </div>

          <div className="d-flex align-items-center gap-2 flex-shrink-0">
            <button onClick={toggleTheme} aria-label="Toggle dark mode"
              style={{ width: 34, height: 34, borderRadius: 9, border: "1px solid var(--fc-border)", background: "var(--fc-card-bg)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--fc-text-muted)", fontSize: 14, transition: "all 0.15s" }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = "#2563EB"; e.currentTarget.style.color = "#2563EB"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--fc-border)"; e.currentTarget.style.color = "var(--fc-text-muted)"; }}>
              <i className={`fa-solid ${isDark ? "fa-sun" : "fa-moon"}`} />
            </button>
          </div>
        </header>

        <main className="fc-page">{children}</main>
      </div>

      {/* Click-away */}
      {profileOpen && (
        <div className="position-fixed top-0 start-0 w-100 h-100" style={{ zIndex: 49 }}
          onClick={() => setProfileOpen(false)} />
      )}
    </div>
  );
}